interface ChatMessage {
  type: 'question' | 'answer';
  content: string;
  timestamp: string;
  tableData?: {
    type: string;
    data: string;
    title?: string;
  };
}

interface WebSocketMessageEvent {
  data: string;
}

class ChatApiService {
  private socket: WebSocket | null = null;
  private messageCallbacks: ((message: ChatMessage) => void)[] = [];
  private connectionCallbacks: ((connected: boolean) => void)[] = [];
  private authToken: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectTimeout: number = 2000; // Start with 2 seconds
  private reconnectTimer: number | null = null;
  private apiUrl: string;
  private defaultApiUrl: string = 'wss://7pumfc2f80.execute-api.eu-west-1.amazonaws.com/dev';
  private defaultAuthToken: string = 'q2Sw3xDZaGZd';

  constructor() {
    // Get custom API URL and auth token from localStorage if available
    const savedApiUrl = localStorage.getItem('chatApiUrl');
    const savedAuthToken = localStorage.getItem('chatAuthToken');

    this.apiUrl = savedApiUrl || this.defaultApiUrl;
    this.authToken = savedAuthToken || this.defaultAuthToken;

    // Don't automatically connect on initialization
    // User must explicitly call connect()
  }

  public connect(customUrl?: string, customToken?: string): void {
    try {
      // Close existing connection if any
      if (this.socket) {
        this.socket.close();
      }

      // Update API URL if provided
      if (customUrl) {
        this.apiUrl = customUrl;
        localStorage.setItem('chatApiUrl', customUrl);
      }

      // Update auth token if provided
      if (customToken) {
        this.authToken = customToken;
        localStorage.setItem('chatAuthToken', customToken);
      }

      const wsUrl = `${this.apiUrl}/?authtoken=${this.authToken}`;
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);

      console.log('Attempting to connect to WebSocket...');
    } catch (error) {
      console.error('Error initializing WebSocket connection:', error);
      this.notifyConnectionChange(false);
      this.scheduleReconnect();
    }
  }

  private handleOpen(): void {
    console.log('WebSocket connection established successfully');
    this.reconnectAttempts = 0;
    this.notifyConnectionChange(true);

    // Send a ping to test the connection
    this.ping();
  }

  private handleMessage(event: WebSocketMessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      console.log('Received message from WebSocket:', data);

      // Process the received message based on the expected response format
      if (data.response) {
        // Extract the summary from the response
        const summary = data.response.summary;
        let content = summary || 'No response received';

        // Check if there's data to display
        if (data.response.data) {
          content += '\n\nHere are the results:';

          // If there's SQL, include it
          if (data.response.generated_sql) {
            content += `\n\nSQL Query: ${data.response.generated_sql}`;
          }

          // Create a table data object to display the results if needed
          const tableData = {
            type: 'json',
            data: data.response.data, // Pass raw data without stringifying
            title: 'Query Results'
          };

          const chatMessage: ChatMessage = {
            type: 'answer',
            content: content,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            tableData: tableData
          };

          this.notifyMessageReceived(chatMessage);
        } else {
          // Simple text response
          const chatMessage: ChatMessage = {
            type: 'answer',
            content: content,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };

          this.notifyMessageReceived(chatMessage);
        }
      } else if (data.summary && data.data) {
        // Handle direct response format without nested 'response' object
        let content = data.summary || 'No response received';

        if (data.generated_sql) {
          content += `\n\nSQL Query: ${data.generated_sql}`;
        }

        // Create a table data object
        const tableData = {
          type: 'json',
          data: data.data, // Pass raw data without stringifying
          title: 'Query Results'
        };

        const chatMessage: ChatMessage = {
          type: 'answer',
          content: content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          tableData: tableData
        };

        this.notifyMessageReceived(chatMessage);
      } else if (data.message) {
        // Fallback for simpler message format
        const chatMessage: ChatMessage = {
          type: 'answer',
          content: data.message,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        this.notifyMessageReceived(chatMessage);
      }
    } catch (error) {
      console.error('Error processing WebSocket message:', error, event.data);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log(`WebSocket connection closed: [${event.code}] ${event.reason}`);
    this.notifyConnectionChange(false);

    // Only attempt auto-reconnect if it wasn't a normal closure (1000)
    // AND it wasn't a user-initiated closure (1005)
    if (event.code !== 1000 && event.code !== 1005) {
      this.scheduleReconnect();
    }
  }

  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    this.notifyConnectionChange(false);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(30000, this.reconnectTimeout * Math.pow(1.5, this.reconnectAttempts - 1));

      console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

      this.reconnectTimer = window.setTimeout(() => {
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        this.connect();
      }, delay);
    } else {
      console.error('Maximum reconnect attempts reached. Please refresh the page to reconnect.');
    }
  }

  public sendMessage(message: string, conversationId?: number): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send message: WebSocket is not connected');
      // Don't automatically reconnect - let the UI handle the reconnection
      return;
    }

    try {
      // Generate a unique ID for this message
      const messageId = crypto.randomUUID ? crypto.randomUUID() : `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Create the base payload
      const payload: any = {
        "action": "message",
        "user_input": message,
        "type": "user_input",
        "id": messageId
      };

      // Add conversation ID if provided
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      // Send the message
      this.socket.send(JSON.stringify(payload));
      console.log('Message sent:', message, 'with ID:', messageId, conversationId ? `for conversation: ${conversationId}` : '');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }

  public ping(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot ping: WebSocket is not connected');
      return;
    }

    try {
      const payload = JSON.stringify({
        action: 'ping'
      });

      this.socket.send(payload);
      console.log('Ping sent to WebSocket server');
    } catch (error) {
      console.error('Error sending ping:', error);
    }
  }

  public onMessage(callback: (message: ChatMessage) => void): () => void {
    this.messageCallbacks.push(callback);

    // Return a function to unsubscribe
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(cb => cb !== callback);
    };
  }

  public onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionCallbacks.push(callback);

    // Return a function to unsubscribe
    return () => {
      this.connectionCallbacks = this.connectionCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyMessageReceived(message: ChatMessage): void {
    this.messageCallbacks.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in message callback:', error);
      }
    });
  }

  private notifyConnectionChange(connected: boolean): void {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in connection callback:', error);
      }
    });
  }

  public isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  public disconnect(): void {
    // Clear any pending reconnect attempts
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Close the socket with normal closure code
    if (this.socket) {
      try {
        this.socket.close(1000, 'User disconnected');
        console.log('WebSocket disconnected by user');
      } catch (error) {
        console.error('Error disconnecting WebSocket:', error);
      }
      this.socket = null;
    }

    // Explicitly notify UI of disconnection
    this.notifyConnectionChange(false);

    // Reset reconnect attempts to ensure clean state for next connection
    this.reconnectAttempts = 0;
  }

  // Get the current API URL
  public getApiUrl(): string {
    return this.apiUrl;
  }

  // Get the default API URL
  public getDefaultApiUrl(): string {
    return this.defaultApiUrl;
  }

  // Reset to the default API URL
  public resetApiUrl(): void {
    this.apiUrl = this.defaultApiUrl;
    localStorage.removeItem('chatApiUrl');
  }

  // Get the current auth token
  public getAuthToken(): string {
    return this.authToken;
  }

  // Get the default auth token
  public getDefaultAuthToken(): string {
    return this.defaultAuthToken;
  }

  // Reset to the default auth token
  public resetAuthToken(): void {
    this.authToken = this.defaultAuthToken;
    localStorage.removeItem('chatAuthToken');
  }

  // Reset both URL and auth token to defaults
  public resetToDefaults(): void {
    this.resetApiUrl();
    this.resetAuthToken();
  }
}

// Create a singleton instance
const chatApiService = new ChatApiService();

// Add a diagnostic helper to verify it's the same instance
console.log('ChatApiService singleton created with ID:', Math.random().toString(36).substring(2, 10));

export default chatApiService;